import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { NativeAlertsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConfiguration } from '@frontend/sports/types/components/native-alert';
import { map } from 'rxjs';

import { extractIds } from './alert-utils';
import { AlertMessage } from './model';

export const enum EnabledAccessPoints {
    Edp = 'EDP',
    MyBets = 'MyBets',
    BetReceipt = 'BetReceipt',
}

@Injectable({ providedIn: 'root' })
export class AlertConfigService {
    private readonly sitecore = inject(Sitecore);
    private readonly nativeAlertsConfig = inject(NativeAlertsConfig);

    readonly minuteInMilliseconds = 60000;
    readonly defaultFetchingInterval = 10;

    private readonly nativeAlertsConfigSignal = toSignal(this.nativeAlertsConfig.whenReady.pipe(map(() => this.nativeAlertsConfig)), {
        initialValue: undefined,
    });

    readonly alertsEnabled = computed(() => this.nativeAlertsConfigSignal()?.enabled ?? false);

    readonly fetchingInterval = computed(
        () => (this.nativeAlertsConfigSignal()?.fetchingInterval ?? this.defaultFetchingInterval) * this.minuteInMilliseconds,
    );

    readonly isAppNotificationsEnabled = computed(() => this.nativeAlertsConfigSignal()?.appNotificationsEnabledCheck ?? false);

    readonly alertMessagesBySport = computed(() => {
        const messagesBySport = new Map<number, AlertMessage[]>();

        if (!this.alertsEnabled()) {
            return messagesBySport;
        }

        for (const [sportId, sportConfig] of this.alertsPerSport()) {
            const alertMessages: AlertMessage[] = [];

            const sports = this.sitecore.nativeAlerts?.sports.find((x) => x.name.toLowerCase() === sportConfig.name.toLowerCase());

            const messages = sports?.nativeAlerts;
            if (!messages) {
                continue;
            }

            for (const alert of sportConfig.alerts) {
                const name = messages[alert.name as keyof typeof messages];
                if (name) {
                    alertMessages.unshift({
                        ids: extractIds(alert),
                        key: alert.name,
                        name,
                        isTop: Boolean(alert.isTop),
                    });
                }
            }

            messagesBySport.set(sportId, alertMessages);
        }

        return messagesBySport;
    });

    readonly topIdsBySport = computed(() => {
        const idsBySport = new Map<number, number[]>();

        for (const [sportId, config] of this.alertsPerSport()) {
            const ids = config.alerts.filter((alert) => alert.isTop).reduce((array, alert) => array.concat(extractIds(alert)), [] as number[]);

            idsBySport.set(sportId, ids);
        }

        return idsBySport;
    });

    readonly alertsPerSport = computed(() => {
        const alertsPerSport = new Map<number, SportConfiguration>();
        const config = this.nativeAlertsConfigSignal();

        if (!config?.sports.length) {
            return alertsPerSport;
        }

        for (const alertsForSport of config.sports) {
            alertsPerSport.set(alertsForSport.id, alertsForSport);
        }

        return alertsPerSport;
    });

    readonly enabledAccessPoints = computed(() => {
        if (!this.alertsEnabled()) {
            return [];
        }

        const config = this.nativeAlertsConfigSignal();
        return config?.enabledAccessPoints ?? [];
    });
}
